/**
 * Day 88.5 P2 — ProductPickerTypeahead.tsx
 *
 * Server-side ILIKE typeahead for product selection. First typeahead in the
 * Arbi codebase — convention seat for future entity pickers at scale.
 *
 * Pattern reference: mirrors AddProductsModal.tsx server-side ILIKE pattern
 * (L57-72) per Day 76 P5.1 Phase 9A escapeOrIlikeTerm adoption.
 *
 * Diverges from AddProductsModal in 3 ways (V1 hardening; convention seat):
 * 1. 300ms debounce locked (matches AddProductsModal house value per
 *    Day 88.5 Phase 0 Q2 disposition).
 * 2. AbortController-backed `.abortSignal()` for in-flight request
 *    cancellation on rapid keystrokes. AddProductsModal accepts the race;
 *    this typeahead is convention seat for race mitigation.
 * 3. Keyboard nav (arrow up/down + Enter to select + Escape to close +
 *    click-outside close) — extends ArbiSelect's Escape-only pattern per
 *    COMPONENT_STANDARDS §8 typeahead convention.
 *
 * Search columns: isg_sku + title + brand (omits upc + asin from
 * AddProductsModal pattern per Day 88.5 P0 Q2 — operator-facing params
 * editing doesn't need inventory identifiers).
 *
 * Component is self-contained: when `value` is set externally (e.g.,
 * URL deep-link from Day 88.5 P3 ProductDetail integration), the effect
 * fetches the product label by id so the picker displays the pre-selected
 * product without requiring caller to pass display text.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ChangeEvent as ReactChangeEvent } from 'react';
import { supabase } from '../../lib/supabase';
import { escapeOrIlikeTerm } from '../../lib/postgrestEscape';

interface ProductPickerResult {
  id: string;
  isg_sku: string;
  title: string | null;
  brand: string | null;
}

interface ProductPickerTypeaheadProps {
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

const MAX_RESULTS = 10;
const DEBOUNCE_MS = 300;

export default function ProductPickerTypeahead({
  value,
  onChange,
  placeholder = 'Search products...',
  disabled,
}: ProductPickerTypeaheadProps): JSX.Element {
  const [search, setSearch] = useState<string>('');
  const [results, setResults] = useState<ProductPickerResult[]>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Self-contained label fetch when `value` prop is set externally
  // (URL deep-link from Day 88.5 P3 ProductDetail integration).
  useEffect(() => {
    let cancelled = false;
    async function loadSelectedLabel(): Promise<void> {
      if (!value) {
        setSelectedLabel(null);
        return;
      }
      const { data, error } = await supabase
        .from('products')
        .select('isg_sku, title')
        .eq('id', value)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setSelectedLabel(null);
        return;
      }
      const label = data.title ? `${data.isg_sku} — ${data.title}` : data.isg_sku;
      setSelectedLabel(label);
    }
    void loadSelectedLabel();
    return () => {
      cancelled = true;
    };
  }, [value]);

  // Search executor — AbortController cancels prior in-flight requests on
  // rapid keystrokes (convention seat per Sol Phase 2 race-mitigation lock).
  const doSearch = useCallback(async (term: string): Promise<void> => {
    const trimmed = term.trim();
    if (!trimmed) {
      setResults([]);
      setSearching(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const ctrl = abortRef.current;
    setSearching(true);
    const t = escapeOrIlikeTerm(trimmed);
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, isg_sku, title, brand')
        .or(`isg_sku.ilike.${t},title.ilike.${t},brand.ilike.${t}`)
        .order('isg_sku', { ascending: true })
        .limit(MAX_RESULTS)
        .abortSignal(ctrl.signal);
      if (ctrl.signal.aborted) return;
      if (!error && data) {
        setResults(data as ProductPickerResult[]);
      } else {
        setResults([]);
      }
    } catch {
      // AbortController throws on .abort(); other network errors silently
      // clear results. V1 doesn't surface error state — future hardening
      // could add an onError callback prop.
      if (!ctrl.signal.aborted) setResults([]);
    }
    if (!ctrl.signal.aborted) setSearching(false);
  }, []);

  function handleSearchChange(e: ReactChangeEvent<HTMLInputElement>): void {
    const v = e.target.value;
    setSearch(v);
    setIsOpen(true);
    setHighlightIndex(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void doSearch(v), DEBOUNCE_MS);
  }

  // Cleanup on unmount — abort in-flight + clear pending debounce.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Click-outside close.
  useEffect(() => {
    if (!isOpen) return undefined;
    function handleClickOutside(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  function handleSelect(product: ProductPickerResult): void {
    onChange(product.id);
    const label = product.title ? `${product.isg_sku} — ${product.title}` : product.isg_sku;
    setSelectedLabel(label);
    setSearch('');
    setResults([]);
    setIsOpen(false);
    setHighlightIndex(-1);
  }

  function handleClear(): void {
    onChange(null);
    setSelectedLabel(null);
    setSearch('');
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  }

  function handleKeyDown(e: ReactKeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      setHighlightIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex >= 0 && highlightIndex < results.length) {
        handleSelect(results[highlightIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setHighlightIndex(-1);
    }
  }

  // Derived display — when value set + not opened for re-search, show the
  // selected label; otherwise show the search term.
  const inputDisplay = isOpen || !value ? search : (selectedLabel ?? '');

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputDisplay}
          onChange={handleSearchChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={value ? '' : placeholder}
          disabled={disabled}
          aria-label="Product search"
          className="arbi-input w-full text-sm pr-7"
        />
        {value && !isOpen && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink-bright text-xs"
            aria-label="Clear selection"
            disabled={disabled}
          >
            ×
          </button>
        )}
      </div>
      {isOpen && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-surface-2 border border-border rounded-md shadow-lg max-h-72 overflow-y-auto">
          {searching && <div className="px-3 py-2 text-xs text-ink-muted italic">Searching…</div>}
          {!searching && results.length === 0 && search.trim() && (
            <div className="px-3 py-2 text-xs text-ink-muted italic">No products found</div>
          )}
          {!searching && results.length === 0 && !search.trim() && (
            <div className="px-3 py-2 text-xs text-ink-muted italic">
              Type to search products by SKU, title, or brand
            </div>
          )}
          {results.map((p, idx) => (
            <button
              key={p.id}
              type="button"
              onMouseDown={(e) => {
                // Use mousedown not click — prevents input blur firing
                // click-outside close before selection completes.
                e.preventDefault();
                handleSelect(p);
              }}
              onMouseEnter={() => setHighlightIndex(idx)}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-baseline gap-2 ${
                idx === highlightIndex
                  ? 'bg-accent/15 text-ink-bright'
                  : 'text-ink-primary hover:bg-surface-3'
              }`}
            >
              <span className="font-mono text-accent">{p.isg_sku}</span>
              <span className="truncate flex-1">{p.title || '—'}</span>
              {p.brand && <span className="text-ink-muted text-2xs">{p.brand}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
